import { IOrderItem } from './models/order-item.model';
import {
  EPricingRuleType,
  EQuantityCalculation,
  getTicketPricingRuleBracket,
  ICalcProductPricingRule
} from '@roller/booking-calculation-service';
import { Injectable } from '@angular/core';
import { ProductHelperService } from '../product/product-helper.service';
import { DateFormatService } from '../date/date-format.service';
import { IVenueConfig } from '../venue-config/models/venue-config.model';

@Injectable({
  providedIn: 'root'
})
export class OrderPricingRuleService {
  constructor(
    private productHelperService: ProductHelperService,
    private dateFormatService: DateFormatService
  ) {}

  /** Adds extra data for the booking calculation service to determine the item price */
  getItemsWithPricingRuleData(
    orderItems: IOrderItem[],
    venueConfig: IVenueConfig,
    bookingCreatedDate: Date | undefined
  ): IOrderItem[] {
    return orderItems.map((item) => {
      if (!item.product.pricingRule) {
        return item;
      }

      switch (item.product.pricingRule.type) {
        case EPricingRuleType.quantity: {
          item.pricingRule = this.getItemPricingRule(item.product.pricingRule, orderItems, item);

          return {
            ...item,
            pricingRuleBracket: getTicketPricingRuleBracket(item)
          };
        }
        case EPricingRuleType.time: {
          return {
            ...item,
            sessionStart: item.session ? this.dateFormatService.hourStringToNumber(item.session.startTime) : undefined
          };
        }
        case EPricingRuleType.earlyBird: {
          const timezoneOffset: number = venueConfig.venueSettings.timeZoneOffsetMinutes ?? 0;
          const currentVenueTime = this.dateFormatService.currentVenueTime(timezoneOffset);

          return {
            ...item,
            venueTime: currentVenueTime,
            bookingCreatedDate: bookingCreatedDate
          };
        }
        default:
          return item;
      }
    });
  }

  private getItemPricingRule(
    pricingRule: ICalcProductPricingRule,
    orderItems: IOrderItem[],
    item: IOrderItem
  ): ICalcProductPricingRule {
    return {
      ...pricingRule,
      perProductAmount: this.getPerProductAmount(orderItems, item)
    };
  }

  /** Calculates the total quantity of tickets in the basket used to determine the current pricing rule bracket. */
  private getPerProductAmount(items: IOrderItem[], item: IOrderItem): number | undefined {
    if (
      item.pricingRule?.quantityCalculation !== EQuantityCalculation.perProduct &&
      item.pricingRule?.quantityCalculation !== EQuantityCalculation.ticketTotal
    ) {
      return undefined;
    }

    return items
      .filter(
        (orderItem) =>
          orderItem.parentProductId === item.parentProductId &&
          this.dateFormatService.isSameDay(orderItem.bookingDate, item.bookingDate) &&
          (!orderItem.session || this.productHelperService.isSameSession(orderItem.session, item.session))
      )
      .reduce((total, i) => total + i.quantity, 0);
  }
}
